import ReactGA from "react-ga4";
import mixpanel from "mixpanel-browser";

const PRODUCTION_ANALYTICS_HOST = "lettucetalk.imoonshots.com";
const PREVIEW_ANALYTICS_HOSTS = new Set([
  "lettucetalk.pages.dev",
  "beta.lettucetalk.pages.dev",
  "test-preview.lettucetalk.pages.dev",
]);
const GA_MEASUREMENT_ID = "G-L4B73QXMEH";
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || "";
const ANALYTICS_IN_DEV = process.env.REACT_APP_ENABLE_ANALYTICS_IN_DEV === "true";

let analyticsEnabled = false;
let mixpanelInitialized = false;
let gaInitialized = false;

export function resolveAnalyticsEnvironment(hostname) {
  if (typeof hostname !== "string") return "unknown";

  const normalizedHostname = hostname
    .trim()
    .toLowerCase()
    .replace(/^\[|\]$/g, "")
    .replace(/\.$/, "");
  if (normalizedHostname === PRODUCTION_ANALYTICS_HOST) return "production";
  if (PREVIEW_ANALYTICS_HOSTS.has(normalizedHostname)) return "preview";
  if (normalizedHostname === "localhost" || normalizedHostname === "::1") {
    return "local";
  }

  const ipv4Parts = normalizedHostname.split(".");
  const isLoopbackIpv4 =
    ipv4Parts.length === 4 &&
    ipv4Parts.every((part) => /^\d+$/.test(part) && Number(part) <= 255) &&
    ipv4Parts[0] === "127";
  return isLoopbackIpv4 ? "local" : "unknown";
}

export function getAnalyticsEnvironment() {
  return resolveAnalyticsEnvironment(
    typeof window !== "undefined" ? window.location.hostname : undefined
  );
}

export const SHOULD_TRACK_ANALYTICS =
  typeof window !== "undefined" &&
  (window.location.hostname === PRODUCTION_ANALYTICS_HOST || ANALYTICS_IN_DEV);

function cleanProperties(properties = {}) {
  return Object.entries(properties).reduce((cleaned, [key, value]) => {
    const normalizedKey = String(key).toLowerCase();
    const isSensitive = [
      "email",
      "host",
      "origin",
      "prompt",
      "query",
      "search",
      "text",
      "note",
      "name",
      "url",
      "token",
      "credential",
      "password",
      "content",
      "thought",
      "question",
      "message",
      "body",
      "draft",
    ].some((part) => normalizedKey.includes(part));

    if (!isSensitive && value !== undefined && value !== null && value !== "") {
      cleaned[key] = value;
    }
    return cleaned;
  }, {});
}

function getBrowserCategory() {
  if (typeof navigator === "undefined") return "unknown";
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    ? "mobile"
    : "desktop";
}

function deleteAnalyticsCookies() {
  if (typeof document === "undefined") return;

  const cookieNames = document.cookie
    .split(";")
    .map((cookie) => cookie.trim().split("=")[0])
    .filter(
      (name) =>
        name === "_ga" ||
        name.startsWith("_ga_") ||
        name.startsWith("mp_")
    );

  cookieNames.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  });
}

function clearMixpanelPersistence() {
  try {
    if (mixpanel.persistence?.clear) {
      mixpanel.persistence.clear();
    }
  } catch {
    // Analytics cleanup must never affect the product experience.
  }
}

export function initializeAnalytics() {
  if (!SHOULD_TRACK_ANALYTICS) return false;

  let initialized = false;

  try {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;

    if (!gaInitialized) {
      ReactGA.initialize(GA_MEASUREMENT_ID, {
        gtagOptions: {
          send_page_view: false,
        },
      });
      gaInitialized = true;
    }
    initialized = gaInitialized;
  } catch {
    // GA failures must not prevent the product or Mixpanel from working.
  }

  if (MIXPANEL_TOKEN) {
    try {
      if (!mixpanelInitialized) {
        mixpanel.init(MIXPANEL_TOKEN, {
          autocapture: false,
          debug: false,
          persistence: "localStorage",
          track_pageview: false,
        });
        mixpanelInitialized = true;
      } else if (typeof mixpanel.clear_opt_in_out_tracking === "function") {
        mixpanel.clear_opt_in_out_tracking();
      }

      if (typeof mixpanel.opt_in_tracking === "function") {
        mixpanel.opt_in_tracking();
      }

      initialized = initialized || mixpanelInitialized;
    } catch {
      // Mixpanel failures must never affect the product experience.
    }
  }

  analyticsEnabled = initialized;
  return initialized;
}

export function disableAnalytics() {
  analyticsEnabled = false;

  try {
    if (typeof window !== "undefined") {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    }

    if (mixpanelInitialized && typeof mixpanel.opt_out_tracking === "function") {
      mixpanel.opt_out_tracking({
        clear_persistence: true,
        persistence_type: "localStorage",
      });
    }

    clearMixpanelPersistence();
    deleteAnalyticsCookies();
  } catch {
    // Analytics must never affect the product experience.
  }
}

export function isAnalyticsEnabled() {
  return analyticsEnabled;
}

export function isMixpanelReady() {
  return analyticsEnabled && mixpanelInitialized;
}

export function getAnalyticsDistinctId() {
  if (!analyticsEnabled || !mixpanelInitialized) return null;

  try {
    const distinctId = mixpanel.get_distinct_id();
    return typeof distinctId === "string" && distinctId.length <= 255
      ? distinctId
      : null;
  } catch {
    return null;
  }
}

export function getAnalyticsRequestMetadata() {
  const analyticsDistinctId = getAnalyticsDistinctId();
  if (!analyticsDistinctId) return {};

  return {
    analyticsConsentGranted: true,
    analyticsDistinctId,
    analyticsEnvironment: getAnalyticsEnvironment(),
  };
}

export function identifySignedInUser(uid) {
  if (!analyticsEnabled || !mixpanelInitialized || !uid) return;

  try {
    mixpanel.identify(uid);
  } catch {
    // Analytics must never affect authentication.
  }
}

export function getAnalyticsUserState(user) {
  return user && user.isAnonymous !== true ? "signed_in" : "anonymous";
}

export function identifyPermanentUserIfReady(user, analyticsReady) {
  if (!analyticsReady || !user || user.isAnonymous === true) return;
  identifySignedInUser(user.uid);
}

export function resetAnalytics() {
  if (!analyticsEnabled || !mixpanelInitialized) return;

  try {
    mixpanel.reset();
  } catch {
    // Analytics must never affect authentication.
  }
}

// Existing GA event API. Legacy callers continue to report to GA only.
export const trackEvent = (category, action, label, value = null) => {
  if (!analyticsEnabled) return;

  try {
    const gaEvent = { category, action, label };
    if (typeof value === "number") gaEvent.value = value;
    ReactGA.event(gaEvent);
  } catch {
    // Analytics must never affect a user action.
  }
};

// Existing GA page-view API. Query strings are intentionally excluded.
export const trackPageView = (page) => {
  if (!analyticsEnabled) return;

  try {
    ReactGA.send({
      hitType: "pageview",
      page: String(page || "").split("?")[0],
    });
  } catch {
    // Analytics must never affect navigation.
  }
};

export function trackMixpanelEvent(eventName, properties = {}) {
  if (!isMixpanelReady()) return false;

  try {
    const cleanedProperties = cleanProperties(properties);
    cleanedProperties.environment = getAnalyticsEnvironment();
    mixpanel.track(eventName, cleanedProperties);
    return true;
  } catch {
    // Analytics must never affect a user action.
    return false;
  }
}

export function trackPurchaseClick({ surface = "unknown", placement = "order_button", partnerId } = {}) {
  trackMixpanelEvent("Purchase Clicked", {
    product: "physical_deck",
    surface,
    placement,
    partner_id: partnerId,
  });
}

export function getSafeAuthFailureProperties({
  authProvider,
  authStep,
  errorCode,
  userState,
}) {
  const safeErrorCode =
    typeof errorCode === "string" && /^[A-Za-z0-9/_-]{1,120}$/.test(errorCode)
      ? errorCode
      : undefined;

  return cleanProperties({
    auth_provider: authProvider,
    auth_step: authStep,
    error_code: safeErrorCode,
    browser: getBrowserCategory(),
    user_state: userState,
  });
}
